import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Plus, X } from "lucide-react";
import {
  type OutputFormat,
  type TemplateCategory,
  type UpsertReportTemplatePayload,
} from "@/api/reportTemplates";
import { documentsApi } from "@/api/documents";
import { useRoles } from "@/hooks/useRoles";
import { useCategories } from "@/hooks/useCategories";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { MasterDocumentPicker } from "@/components/reports/MasterDocumentPicker";
import { PromptBuilderButton } from "./PromptBuilderModal";

const OUTPUT_FORMATS: OutputFormat[] = ["Word", "Excel", "Pdf"];
const PLACEHOLDERS = [
  "loan_number",
  "borrower_name",
  "loan_amount",
  "seller_name",
  "business_name",
  "broker_name",
  "portfolio_name",
  "current_date",
  "deal_id",
  "form_responses",
  "master_document",
  "supporting_documents",
  "branding_guide",
] as const;

interface ReportTemplateEditBasicsProps {
  form: UpsertReportTemplatePayload;
  setForm: React.Dispatch<React.SetStateAction<UpsertReportTemplatePayload>>;
  /** Existing template ID, or `null` when the page is at /admin/report-templates/new. */
  templateId: string | null;
}

/**
 * LLM &amp; Reports v3.1 §10d — basics section of the admin edit page.
 * Owns name, description, category, output format, role assignments,
 * optional master document, prompt body (with placeholder chips),
 * instructions, and is-active toggle.
 *
 * AI Prompt Builder v1.0 §5a — the "✨ Help me write this" button sits
 * next to the Prompt Body label, gated by Reports.DraftPrompts.
 */
export function ReportTemplateEditBasics({ form, setForm, templateId }: ReportTemplateEditBasicsProps) {
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Admin-managed Lookups v1.0 §4d — read role + category labels from
  // the canonical lookup endpoints (active rows only). When an admin
  // renames or archives an entry on /admin/roles or /admin/categories
  // the relevant React Query cache key invalidates and this editor
  // picks up the new values without a page reload.
  const rolesQuery = useRoles();
  const allRoles = (rolesQuery.data ?? []).map((r) => ({
    code: r.code,
    title: r.displayName,
  }));

  const categoriesQuery = useCategories();
  // Active categories drive the new-template dropdown. If the form is
  // editing an existing template whose category was archived after
  // creation, surface that code in the dropdown anyway so the editor
  // can render it — labelled "(archived)" — and an admin can choose
  // to migrate the template to an active category.
  const activeCategoryCodes = (categoriesQuery.data ?? []).map((c) => c.code);
  const categoryOptions = (categoriesQuery.data ?? []).map((c) => ({
    code: c.code,
    label: c.displayName,
    archived: false,
  }));
  if (form.category && !activeCategoryCodes.includes(form.category)) {
    categoryOptions.push({
      code: form.category,
      label: `${form.category} (archived)`,
      archived: true,
    });
  }

  const masterDoc = useQuery({
    queryKey: ["central-document", form.masterDocumentId],
    queryFn: () => documentsApi.get(form.masterDocumentId!),
    enabled: !!form.masterDocumentId,
  });

  const insertPlaceholder = (token: string) => {
    const ta = promptRef.current;
    const wrapped = `{{${token}}}`;
    if (!ta) {
      setForm((f) => ({ ...f, promptBody: f.promptBody + wrapped }));
      return;
    }
    const start = ta.selectionStart ?? form.promptBody.length;
    const end = ta.selectionEnd ?? form.promptBody.length;
    const next =
      form.promptBody.slice(0, start) + wrapped + form.promptBody.slice(end);
    setForm((f) => ({ ...f, promptBody: next }));
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = start + wrapped.length;
      ta.setSelectionRange(cursor, cursor);
    });
  };

  const toggleRole = (code: string) => {
    setForm((f) => ({
      ...f,
      roleCodes: f.roleCodes.includes(code)
        ? f.roleCodes.filter((r) => r !== code)
        : [...f.roleCodes, code],
    }));
  };

  return (
    <section className="bg-white-pure border border-border-light rounded-lg p-space-4 space-y-space-3">
      <h2 className="font-display font-bold text-h3 text-onyx tracking-normal">
        <span className="text-gold-shadow mr-space-2 font-mono">1.</span>
        Basics
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
        <FormField label="Name" required htmlFor="rt-name">
          <Input
            id="rt-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </FormField>
        <FormField label="Category" htmlFor="rt-cat">
          <Select
            id="rt-cat"
            value={form.category}
            onChange={(e) =>
              setForm({ ...form, category: e.target.value as TemplateCategory })
            }
          >
            {categoryOptions.length === 0 ? (
              <option value={form.category}>{form.category}</option>
            ) : (
              categoryOptions.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))
            )}
          </Select>
        </FormField>
        <FormField label="Output format" htmlFor="rt-fmt">
          <Select
            id="rt-fmt"
            value={form.outputFormat}
            onChange={(e) =>
              setForm({ ...form, outputFormat: e.target.value as OutputFormat })
            }
          >
            {OUTPUT_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </Select>
        </FormField>
        <FormField label="Description" htmlFor="rt-desc">
          <Input
            id="rt-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </FormField>
      </div>

      <FormField
        label="Role assignments"
        helperText="Pick the role codes that can see this template in their Reports menu."
      >
        <div className="flex flex-wrap gap-space-1 mt-space-1">
          {allRoles.length === 0 ? (
            <span className="text-caption text-gray-mid">Loading roles…</span>
          ) : (
            allRoles.map(({ code, title }) => {
              const on = form.roleCodes.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleRole(code)}
                  title={`${code} — ${title}`}
                  aria-label={`${code} — ${title}`}
                  className={`max-w-[16rem] truncate px-space-2 py-[4px] rounded-full text-caption font-display font-semibold border transition-colors ${
                    on
                      ? "bg-gold/15 border-gold text-gold-shadow"
                      : "bg-white-pure border-border-light text-gray-mid hover:border-gold-shadow"
                  }`}
                  aria-pressed={on}
                >
                  <span className="font-mono">{code}</span>
                  <span className="mx-space-1" aria-hidden>·</span>
                  <span>{title}</span>
                </button>
              );
            })
          )}
        </div>
      </FormField>

      <FormField
        label="Master document (optional)"
        helperText="Pulls from the central document repository. Pre-loaded for users on the generate page."
      >
        {form.masterDocumentId ? (
          <div className="flex items-center justify-between gap-space-2 border border-border-light rounded-md p-space-2 bg-white-warm">
            <div className="flex items-center gap-space-2 flex-1 min-w-0">
              <FileText
                size={18}
                aria-hidden
                className="text-gold-shadow shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-onyx truncate">
                  {masterDoc.data?.title ?? "Loading…"}
                </div>
                <div className="text-caption text-gray-mid truncate">
                  {masterDoc.data
                    ? `${(masterDoc.data.fileExtension ?? "").replace(/^\./, "").toUpperCase() || masterDoc.data.contentType} · ${masterDoc.data.category}`
                    : form.masterDocumentId}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-space-1 shrink-0">
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => setPickerOpen(true)}
              >
                Change
              </Button>
              <Button
                type="button"
                variant="tertiary"
                size="sm"
                onClick={() => setForm({ ...form, masterDocumentId: null })}
                aria-label="Remove master document"
              >
                <X size={14} aria-hidden />
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="secondary"
            onClick={() => setPickerOpen(true)}
          >
            <FileText size={14} aria-hidden /> Choose Document
          </Button>
        )}
      </FormField>
      <MasterDocumentPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selectedId={form.masterDocumentId}
        onPick={(doc) => setForm({ ...form, masterDocumentId: doc.id })}
      />

      {/* Prompt body — uses a custom label row so the AI Prompt Builder
          trigger ("✨ Help me write this") can sit at the right of the
          label without nesting a button inside an HTML <label> element. */}
      <div className="flex flex-col">
        <div className="flex items-center justify-between mb-[6px] gap-space-2">
          <label
            htmlFor="rt-prompt"
            className="font-display text-label font-semibold uppercase tracking-label text-gray-mid"
          >
            Prompt body
            <span className="text-gold ml-1" aria-hidden>
              *
            </span>
          </label>
          <PromptBuilderButton
            templateId={templateId}
            form={form}
            onAcceptDraft={(drafted) =>
              setForm((f) => ({ ...f, promptBody: drafted }))
            }
          />
        </div>
        <Textarea
          id="rt-prompt"
          ref={promptRef}
          rows={10}
          value={form.promptBody}
          onChange={(e) => setForm({ ...form, promptBody: e.target.value })}
          className="font-mono text-caption"
        />
        <div className="flex flex-wrap gap-space-1 mt-space-2">
          {PLACEHOLDERS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => insertPlaceholder(p)}
              className="inline-flex items-center gap-space-1 px-space-2 py-[4px] rounded-md text-caption font-mono bg-white-warm border border-border-light hover:border-gold-shadow"
            >
              <Plus size={10} aria-hidden /> {`{{${p}}}`}
            </button>
          ))}
        </div>
        <p className="text-label text-gray-mid mt-[6px]">
          Click a placeholder chip to insert it at the cursor.
        </p>
      </div>

      <FormField
        label="Instructions (optional)"
        htmlFor="rt-inst"
        helperText="Shown to users at the top of the form when they fill it out."
      >
        <Textarea
          id="rt-inst"
          rows={3}
          value={form.instructions ?? ""}
          onChange={(e) => setForm({ ...form, instructions: e.target.value })}
        />
      </FormField>

      <div className="flex items-center justify-between mt-space-2">
        <label className="flex items-center gap-space-2 text-small">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            className="h-4 w-4 accent-gold"
          />
          Active — users in entitled roles can pick this template
        </label>
        <div className="text-caption text-gray-mid">
          {form.roleCodes.length} role
          {form.roleCodes.length === 1 ? "" : "s"} ·{" "}
          <Badge status={form.isActive ? "finalized" : "archived"}>
            {form.isActive ? "Active" : "Inactive"}
          </Badge>
        </div>
      </div>
    </section>
  );
}
